<template>
  <div>
    <div class="form__content">
      <h1 class="extra-large-headline">
        {{ $t("CcSelfieForm.label.headline") }}
      </h1>
      <p
        class="bodycopy"
        id="bodyCopy"
        v-html="
          $t('CcSelfieForm.label.body', {
            entity: `<b>${form.selfieImgContent.entity}</b>`,
          })
        "
      ></p>
      <ul class="form__selfie-instructions bodycopy">
        <li>{{ $t("CcSelfieForm.ul.list") }}</li>
        <li>{{ $t("CcSelfieForm.ul.list2") }}</li>
        <li>{{ $t("CcSelfieForm.ul.list2") }}</li>
        <li>{{ $t("CcSelfieForm.ul.list4") }}</li>

        <li>{{ $t("CcSelfieForm.ul.list5") }}</li>
        <li>{{ $t("CcSelfieForm.ul.list6") }}</li>
        <li>{{ $t("CcSelfieForm.ul.list7") }}</li>
      </ul>
    </div>

    <div class="selfie-content-container">
      <img class="selfie-image" src="@/assets/images/identity/ccSelfie.png" />
      <div class="selfie-content left">
        <p>{{ form.selfieImgContent.entity }}</p>
        <p>{{ form.selfieImgContent.date }}</p>
      </div>
      <div class="selfie-content right">
        <p>{{ form.selfieImgContent.entity }}</p>
        <p>{{ form.selfieImgContent.date }}</p>
      </div>
    </div>

    <form @submit.prevent class="form__content">
      <FileUploadField
        name="selfieDocument"
        :label="$t('FileUploadField.label.selfieDocument')"
        :placeholder="$t('FileUploadField.placeholder.selectOrDrag')"
        iconType="VerificationCircle"
        :errors="form.selfieDocumentErrors"
        @updateFile="updateFiles"
      />

      <ButtonBase
        :isDisabled="formHasErrors || globalStore.getIsFormLoading"
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        {{ $t("ButtonBase.label.sofSelfie") }}
      </ButtonBase>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useGlobalStore } from "@/stores/globalStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import useApiHelpers from "@/composables/api/helpers";
import useCustomerActions from "@/composables/api/useCustomerActions";

import FileUploadField from "@/components/ui/inputs/FileUploadField.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import useFormValidation from "@/composables/validations/useFormValidation";

const route = useRoute();
const router = useRouter();
const globalStore = useGlobalStore();
const identityQueueStore = useIdentityQueueStore();

const { validateFileInput } = useFormValidation();
const { postRequest } = useApiHelpers();
const { fetchCustomerId } = useCustomerActions();
const emit = defineEmits(["onPrimaryButtonClick", "onSecondaryButtonClick"]);
const props = defineProps({
  mode: {
    type: String,
    default: "form",
  },
});
const form = ref({
  docType: "others",
  selfieDocument: [],
  selfieDocumentErrors: "",
  selfieImgContent: {
    entity: "",
    date: null,
  },
});

const updateFiles = (file) => {
  const fileValidationErrors = validateFileInput("selfieDocument", file);
  form.value.selfieDocument = file;

  form.value.selfieDocumentErrors = fileValidationErrors;
};

const handleFormSubmit = async () => {
  const custId = await fetchCustomerId();
  const formData = new FormData();
  const docSubType = "cc_selfie";

  formData.set("documentFile", form.value.selfieDocument);
  formData.set("documentType", form.value.docType);
  formData.set("documentSubType", docSubType);
  formData.set("documentReference", custId);

  globalStore.setIsFormLoading(true);
  postRequest(
    "documents",
    formData,
    () => {
      if (props.mode === "modal") {
        emit("onPrimaryButtonClick");
      } else {
        identityQueueStore.updateToNextRoute();
        identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
          router.push(nextRoute);
        });
      }
    },
    () => {
      if (props.mode === "modal") emit("onPrimaryButtonClick"); // to close the modal on error
    }
  );
};

const addImageTextInstructionsWithCurrentDateAndEntity = () => {
  const date = new Date();
  form.value.selfieImgContent.date = new Intl.DateTimeFormat("en-GB").format(
    date
  );

  const { host } = window.location;
  const isBanxa = ["checkout", "spa", "localhost:3000"];
  if (
    host.split(".").some((hostName) => {
      return isBanxa.includes(hostName);
    })
  ) {
    form.value.selfieImgContent.entity = "Banxa purchase";
  } else if (host.split(".")[0] === "checkout") {
    form.value.selfieImgContent.entity = `${host
      .split(".")
      .slice(1)
      .join(".")} purchase`;
  } else {
    form.value.selfieImgContent.entity = `${host} purchase`;
  }

  document
    .getElementById("bodyCopy")
    .innerHTML.replace("$entity", form.value.selfieImgContent.entity);
  // form.value.setEntityInDescriptionByKycFormType(entityData);
};

const formHasErrors = computed(() => {
  const anyErrors = form.value.selfieDocumentErrors !== "";
  const anyFieldsEmpty = form.value.selfieDocument.length === 0;
  return anyErrors || anyFieldsEmpty;
});

onMounted(() => {
  addImageTextInstructionsWithCurrentDateAndEntity();
});
</script>

<style lang="scss" scoped>
.form__content {
  padding-bottom: 3rem;
  max-width: 54rem;
  padding: 0 2rem;
  :nth-child(1) {
    padding-top: 1rem;
  }
  @include breakpoint($min-m) {
    padding-inline: 0;
  }

  .bodycopy {
    margin-bottom: 1rem;
    @include breakpoint($min-m) {
      margin-bottom: 1.5rem;
    }
  }
  .form__selfie-instructions {
    list-style: unset;
    padding-inline: 1.5rem;

    li {
      font-size: $font-body;
      line-height: 1.5;
    }
  }
}

.selfie-image {
  display: flex;
  max-width: 90%;
  height: 403px;
  position: relative;
  margin: 0 auto;

  @include breakpoint($max-sm) {
    height: 250px;
  }
}
.selfie-content-container {
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: end;
  width: 100%;
  font-size: 10px;
  margin-bottom: 5px;
  color: $black;

  @include breakpoint($max-sm) {
    font-size: 7px;
    min-height: unset;
  }

  .selfie-content {
    width: 50%;
    text-align: center;
    position: absolute;

    p {
      margin: 0;
      max-width: 80px;
      color: $black;
      @include breakpoint($max-sm) {
        font-size: 8px;
      }
    }
    &.left {
      left: 82px;

      @include breakpoint($max-sm) {
        left: 40px;
      }
    }
    &.right {
      right: -33px;
      @include breakpoint($max-sm) {
        right: -5px;
      }
    }
  }
}
</style>
